// In-room Poppy: volume, mute, host remove-player + end-game flows.

import React, { useEffect, useRef, useState } from 'react';
import { Volume2Icon, VolumeXIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  useAudioLevels,
  useMedia,
  useRoomClose,
  useRoomConfig,
  useRoomPlayers,
  useRoute,
  useRoomSocket,
} from '../../hooks';
import type { AudioLevelKey } from '../../hooks';
import PlayerSelector from './PlayerSelector';
import VolumeSliderGroup from '@/components/media/VolumeSliderGroup';
import { analyticsService } from '../../services/analyticsService';
import {
  RoomPlayer,
  RoomPlayerActionType,
  RoomPlayerCommand,
  SenderRoleType,
} from '@airhead-games/shared/types';
import { generateCommandId } from '@airhead-games/shared/utilities';
import { cn } from '@/utilities/cn';

interface AvatarMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const AvatarMenu: React.FC<AvatarMenuProps> = ({ isOpen, onClose }) => {
  const { updateVolumeSettings, currentVolumes, isMuted, toggleMute } =
    useMedia();
  const { setLevel } = useAudioLevels();
  const { roomCode, gameConfig, status } = useRoomConfig();
  const { players, playerCount } = useRoomPlayers();
  const { isHostRoute } = useRoute();
  const { sendCommand } = useRoomSocket();
  const { closeRoom, isClosing } = useRoomClose();

  const [isKickPlayerDialogOpen, setIsKickPlayerDialogOpen] = useState(false);
  const [isEndGameConfirmOpen, setIsEndGameConfirmOpen] = useState(false);
  const [isKickConfirmOpen, setIsKickConfirmOpen] = useState(false);
  const [selectedKickPlayer, setSelectedKickPlayer] =
    useState<RoomPlayer | null>(null);
  const skipNextKickCancelTracking = useRef(false);

  const minPlayers = gameConfig?.gamePlayersMin ?? 0;
  const showPlayerAccess = isHostRoute;
  const noPlayers = players.length === 0;
  const isInLobby = status === 'opened';
  const canRemovePlayerByMin = isInLobby || playerCount - 1 >= minPlayers;
  const canOpenKickFlow =
    showPlayerAccess && !!gameConfig && !noPlayers && canRemovePlayerByMin;
  const kickDisabledReason = noPlayers
    ? 'No players in the room'
    : !canRemovePlayerByMin
      ? `Removing drops below min players`
      : null;

  const poppyMenuAnalyticsParams = {
    surface: 'avatar_menu' as const,
    menu_name: 'poppy_menu',
    link_location: 'avatar_menu',
    room_code: roomCode || 'unknown',
    game_name: gameConfig?.gameName || 'Unknown Game',
    player_count: playerCount,
    source_path:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
  };

  const trackPoppyMenuItem = (
    label: string,
    additionalParams?: Record<
      string,
      string | number | boolean | null | undefined
    >
  ) => {
    analyticsService.trackNavigationItemClick(label, {
      ...poppyMenuAnalyticsParams,
      ...additionalParams,
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setIsEndGameConfirmOpen(false);
      setIsKickConfirmOpen(false);
      setSelectedKickPlayer(null);
      setIsKickPlayerDialogOpen(false);
    }
  }, [isOpen]);

  // Live audio applies immediately; the saved preference follows on the
  // useUserSave debounce.
  const handleVolumeChange = (key: AudioLevelKey, value: number) => {
    updateVolumeSettings({ [key]: value / 100 });
    setLevel(key, value / 100);
  };

  const handleKickPlayerSelect = (playerId: string) => {
    const player = players.find((p) => p.playerId === playerId);
    if (!player) {
      return;
    }
    trackPoppyMenuItem('Remove Player Select', {
      target_player_id: player.playerId,
    });
    setSelectedKickPlayer(player);
    setIsKickConfirmOpen(true);
    setIsKickPlayerDialogOpen(false);
  };

  const handleKickPlayerConfirm = () => {
    if (!selectedKickPlayer || !roomCode) {
      setIsKickConfirmOpen(false);
      setSelectedKickPlayer(null);
      return;
    }

    trackPoppyMenuItem('Remove Player Confirm', {
      target_player_id: selectedKickPlayer.playerId,
    });
    skipNextKickCancelTracking.current = true;

    const kickCommand: RoomPlayerCommand = {
      id: generateCommandId(),
      type: 'RoomPlayerAction',
      action: RoomPlayerActionType.KICK,
      senderRole: SenderRoleType.HOST,
      clientTimestamp: Date.now(),
      roomCode,
      player: selectedKickPlayer,
    };

    sendCommand(kickCommand);
    setIsKickConfirmOpen(false);
    setSelectedKickPlayer(null);
  };

  const handleKickPlayerCancel = () => {
    if (skipNextKickCancelTracking.current) {
      skipNextKickCancelTracking.current = false;
    } else {
      trackPoppyMenuItem('Remove Player Cancel', {
        target_player_id: selectedKickPlayer?.playerId ?? null,
      });
    }
    setIsKickConfirmOpen(false);
    setSelectedKickPlayer(null);
  };

  const handleEndGame = () => {
    trackPoppyMenuItem('End Game Confirm');
    analyticsService.trackButtonClick('End Game', {
      link_location: 'avatar_menu',
      room_code: roomCode || 'unknown',
      game_name: gameConfig?.gameName || 'Unknown Game',
      player_count: playerCount,
    });

    analyticsService.trackRoomClosed({
      room_code: roomCode || 'unknown',
      game_name: gameConfig?.gameName || 'Unknown Game',
    });

    closeRoom();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
            setIsEndGameConfirmOpen(false);
            setIsKickConfirmOpen(false);
            setSelectedKickPlayer(null);
          }
        }}
      >
        <DialogContent
          className={cn(
            'flex max-h-[min(92dvh,92svh)] flex-col gap-4',
            'sm:max-w-2xl'
          )}
          showCloseButton
        >
          <DialogHeader className="items-center gap-3">
            <DialogTitle className="text-center text-2xl font-semibold">
              Volume Controls
            </DialogTitle>
            <DialogDescription className="sr-only">
              Adjust Poppy, music, and effect volumes, or mute everything.
              Changes are saved to your volume controls.
            </DialogDescription>
          </DialogHeader>

          <div className="-mt-1 -mb-1 flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-10"
              disabled={isMuted}
              onClick={() => {
                if (isMuted) {
                  return;
                }
                analyticsService.trackButtonClick('Mute Audio', {
                  ...poppyMenuAnalyticsParams,
                });
                toggleMute();
              }}
              aria-label="Mute all audio"
            >
              <VolumeXIcon className="size-6" />
            </Button>
            <Switch
              checked={!isMuted}
              onCheckedChange={(checked) => {
                analyticsService.trackPreferenceChanged({
                  preference_type: 'volume_mute',
                  preference_value: !checked,
                  link_location: 'avatar_menu',
                });
                toggleMute();
              }}
              aria-label="Toggle mute for all in-room audio"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-10"
              disabled={!isMuted}
              onClick={() => {
                if (!isMuted) {
                  return;
                }
                analyticsService.trackButtonClick('Unmute Audio', {
                  ...poppyMenuAnalyticsParams,
                });
                toggleMute();
              }}
              aria-label="Unmute all audio"
            >
              <Volume2Icon className="size-6" />
            </Button>
          </div>

          <VolumeSliderGroup
            speechVolume={Math.round(currentVolumes.speechVolume * 100)}
            musicVolume={Math.round(currentVolumes.musicVolume * 100)}
            sfxVolume={Math.round(currentVolumes.sfxVolume * 100)}
            onSpeechVolumeChange={(v) => handleVolumeChange('speechVolume', v)}
            onMusicVolumeChange={(v) => handleVolumeChange('musicVolume', v)}
            onSfxVolumeChange={(v) => handleVolumeChange('sfxVolume', v)}
            disabled={isMuted}
          />

          {showPlayerAccess && (
            <section className="mt-1 flex flex-col gap-3 border-t border-border pt-5">
              <h2 className="text-center text-2xl font-semibold">
                More Actions
              </h2>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <Button
                    className="w-full"
                    onClick={() => {
                      trackPoppyMenuItem('Remove Player Open');
                      setIsKickPlayerDialogOpen(true);
                    }}
                    disabled={!canOpenKickFlow}
                  >
                    Remove player
                  </Button>
                  {kickDisabledReason && (
                    <p className="text-center text-sm text-[#ffc74c]">
                      {kickDisabledReason}
                    </p>
                  )}
                </div>
                <Button
                  className="w-full sm:flex-1"
                  onClick={() => {
                    trackPoppyMenuItem('End Game Open');
                    setIsEndGameConfirmOpen(true);
                  }}
                >
                  End game
                </Button>
              </div>
            </section>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isEndGameConfirmOpen}
        onOpenChange={setIsEndGameConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End game</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end the game for all players?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              type="button"
              disabled={isClosing}
              onClick={() => trackPoppyMenuItem('End Game Cancel')}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              variant="destructive"
              disabled={isClosing}
              onClick={handleEndGame}
            >
              End game
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isKickConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleKickPlayerCancel();
          } else {
            setIsKickConfirmOpen(true);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove player</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedKickPlayer
                ? `Are you sure you want to kick ${selectedKickPlayer.playerName} from this game room?`
                : 'Are you sure you want to remove this player from the game room?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              variant="destructive"
              onClick={handleKickPlayerConfirm}
            >
              {selectedKickPlayer
                ? `Remove ${selectedKickPlayer.playerName}`
                : 'Remove player'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={isKickPlayerDialogOpen}
        onOpenChange={setIsKickPlayerDialogOpen}
      >
        <DialogContent showCloseButton className="sm:max-w-md">
          <DialogHeader className="items-center text-center">
            <DialogTitle>Remove player</DialogTitle>
            <DialogDescription>
              Select player to remove them from the game room.
            </DialogDescription>
          </DialogHeader>

          <PlayerSelector
            mode="single"
            players={players}
            selectedPlayerId={selectedKickPlayer?.playerId ?? null}
            onSelect={handleKickPlayerSelect}
            selectedEmotion="angry"
            className="max-h-[45svh]"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AvatarMenu;
